import { TransferOfChargeSearchCriteria } from "domain/entity/TransferOfCharge/TransferOfChargeSearchCriteria";
import { getDefaultBuConfig } from "domain/repository/apiUtil";
import { stateDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { PortConstant } from "presentation/constant/Port/PortConstant";
import { TransferOfChargeConstant } from "presentation/constant/TransferOfCharge/TransferOfChargeConstant";
import { useTransferOfChargeVM } from "presentation/hook/TransferOfCharge/useTransferOfChargeVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useTransferOfChargeTracked } from "presentation/store/TransferOfCharge/TransferOfChargeProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHInputMask, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const TransferOfChargeSearchPanel = () => {
    const SCREEN_CONSTANT = TransferOfChargeConstant;
    const [transferOfChargeState] = useTransferOfChargeTracked();
    const transferOfChargeVM = useTransferOfChargeVM();
    const { searchCriteria, masterState } = transferOfChargeState;
    const { isSaveClicked } = masterState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const blNoMaxLength: number = getDefaultBuConfig<number>("REACT_APP_BILL_OF_LADING_MAX_LENGTH") || 35;

    const resetButtonClicked = useCallback(() => {
        transferOfChargeVM.onSearchCriteriaResetClick();
    }, [transferOfChargeVM]);

    const searchButtonClicked = useCallback(async (searchCriteria: TransferOfChargeSearchCriteria) => {
        //transferOfChargeVM.onSearchButtonClicked();
        //const valid = await transferOfChargeVM.validateSearchCriteria(operatingUnit, searchCriteria);
        //if (!valid) {
        //    return;
        //}
        if (operatingUnit === 'SAPT' || operatingUnit === 'KICT') {
            if (!searchCriteria.blNo1 && !searchCriteria.soNumber1 && !searchCriteria.serviceReferenceNo && (!searchCriteria.cntrNos || searchCriteria.cntrNos?.length === 0 || (searchCriteria.cntrNos?.length === 1 && searchCriteria.cntrNos[0] === ''))) {
                messageBarVM.showError("Container No., Bill of Lading / Ref. No., Shipping Bill, Service Request Ref. must input one of them at least.");
                return;
            }
        } else {
            if (!searchCriteria.blNo1 && !searchCriteria.soNumber1 && (!searchCriteria.cntrNos || searchCriteria.cntrNos?.length === 0 || (searchCriteria.cntrNos?.length === 1 && searchCriteria.cntrNos[0] === ''))) {
                messageBarVM.showError("Container No., Bill of Lading / Ref. No., Shipping Bill must input one of them at least.");
                return;
            }
        }

        setIsLoading(true);
        transferOfChargeVM.setIsSearchLoading(true);
        transferOfChargeVM.onSearch(searchCriteria).then((data) => {
            if (data) {
                messageBarVM.showError(data);
            }
            setIsLoading(false);
            transferOfChargeVM.setIsSearchLoading(false);
        }).catch(error => {
            setIsLoading(false);
            transferOfChargeVM.setIsSearchLoading(false);
        })

    }, [messageBarVM, operatingUnit, transferOfChargeVM])

    const memoSubChgTypeOptions = useMemo(() => {
        if (!searchCriteria.chargeType) {
            return Object.values(transferOfChargeState.dynamicOptions.subChargeTypeDropdownOptions).flat();
        }
        const subChgTypeOptions = searchCriteria.chargeType ? transferOfChargeState.dynamicOptions.subChargeTypeDropdownOptions[searchCriteria.chargeType] : [];
        return subChgTypeOptions;
    }, [searchCriteria.chargeType, transferOfChargeState.dynamicOptions.subChargeTypeDropdownOptions])

    const memoSubServiceRequestCodeOptions = useMemo(() => {
        const subServiceRequestCodeOptions = searchCriteria.serviceRequestCode ? transferOfChargeState.dynamicOptions.subServiceRequestCodeDropdownOptions[searchCriteria.serviceRequestCode] : transferOfChargeState.dynamicOptions.allSubReqServiceCodeDropdownOptions;
        return subServiceRequestCodeOptions;
    }, [searchCriteria.serviceRequestCode, transferOfChargeState.dynamicOptions.allSubReqServiceCodeDropdownOptions, transferOfChargeState.dynamicOptions.subServiceRequestCodeDropdownOptions])

    const memoChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.chargeType || ''}
                fieldValue={searchCriteria?.chargeType}
                fieldLabel={SCREEN_CONSTANT.CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeType'}
                maxLength={60}
                options={transferOfChargeState.dynamicOptions.chargeTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.CHARGE_TYPE, transferOfChargeState.dynamicOptions.chargeTypeDropdownOptions, transferOfChargeVM, searchCriteria?.chargeType])

    const memoSubChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.subChargeType || ''}
                fieldValue={searchCriteria?.subChargeType}
                fieldLabel={SCREEN_CONSTANT.SUB_CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subChargeType'}
                maxLength={60}
                options={memoSubChgTypeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.SUB_CHARGE_TYPE, transferOfChargeVM, searchCriteria?.subChargeType, memoSubChgTypeOptions])


    const memoServiceRequestCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.serviceRequestCode || ''}
                fieldValue={searchCriteria?.serviceRequestCode}
                fieldLabel={SCREEN_CONSTANT.SERVICE_REQUEST_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'serviceRequestCode'}
                maxLength={60}
                options={transferOfChargeState.dynamicOptions.serviceRequestCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.serviceRequestCode, SCREEN_CONSTANT.SERVICE_REQUEST_CODE, transferOfChargeState.dynamicOptions.serviceRequestCodeDropdownOptions, transferOfChargeVM])

    const memoSubServiceRequestCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.subServiceRequestCode || ''}
                fieldValue={searchCriteria?.subServiceRequestCode}
                fieldLabel={SCREEN_CONSTANT.SUBSERVICE_REQUEST_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subServiceRequestCode'}
                maxLength={60}
                options={memoSubServiceRequestCodeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.subServiceRequestCode, SCREEN_CONSTANT.SUBSERVICE_REQUEST_CODE, memoSubServiceRequestCodeOptions, transferOfChargeVM])

    const memoOpsLine = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.opsLine || ''}
                fieldValue={searchCriteria?.opsLine}
                fieldLabel={SCREEN_CONSTANT.OPS_LINE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'opsLine'}
                maxLength={60}
                sort={false}
                options={transferOfChargeState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.opsLine, SCREEN_CONSTANT.OPS_LINE, transferOfChargeState.dynamicOptions.opsLineDropdownOptions, transferOfChargeVM])

    const memoChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.chargeOnCompany || ''}
                fieldValue={searchCriteria?.chargeOnCompany}
                fieldLabel={SCREEN_CONSTANT.CHARGEON_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeOnCompany'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={transferOfChargeState.dynamicOptions.chargeOnCompanyDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.chargeOnCompany, SCREEN_CONSTANT.CHARGEON_COMPANY, transferOfChargeState.dynamicOptions.chargeOnCompanyDropdownOptions, transferOfChargeVM])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.billToCompany || ''}
                fieldValue={searchCriteria?.billToCompany}
                fieldLabel={SCREEN_CONSTANT.BILLTO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompany'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={transferOfChargeState.dynamicOptions.billToCompanyDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.billToCompany, SCREEN_CONSTANT.BILLTO_COMPANY, transferOfChargeState.dynamicOptions.billToCompanyDropdownOptions, transferOfChargeVM])

    const memoState = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.dtlState || ''}
                fieldValue={searchCriteria?.dtlState}
                fieldLabel={SCREEN_CONSTANT.STATE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'dtlState'}
                maxLength={60}
                sort={false}
                options={stateDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.dtlState, SCREEN_CONSTANT.STATE, transferOfChargeVM])

    const memoOpsDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "470px", width: "100%", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.OPS_DATE_RANGE}
                width='100%'
                fieldNames={{ startField: "opsDateFrom", endField: "opsDateTo" }}
                dateRange={{ startDate: searchCriteria.opsDateFrom, endDate: searchCriteria.opsDateTo }}
                onDatesChange={transferOfChargeVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.OPS_DATE_RANGE, transferOfChargeVM.onDateRangeChange, searchCriteria.opsDateFrom, searchCriteria.opsDateTo])


    const memoBlNo1 = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.blNo1 ?? ''}
                fieldValue={searchCriteria?.blNo1}
                // fieldLabel={(operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? SCREEN_CONSTANT.BILL_OF_LADING__REF_NO : SCREEN_CONSTANT.BILL_OF_LADING}
                fieldLabel={SCREEN_CONSTANT.BILL_OF_LADING__REF_NO}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'blNo1'}
                maxLength={blNoMaxLength}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.blNo1, SCREEN_CONSTANT.BILL_OF_LADING__REF_NO, isSaveClicked, blNoMaxLength, transferOfChargeVM])

    const memoSoNumber1 = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.soNumber1 ?? ''}
                fieldValue={searchCriteria?.soNumber1}
                fieldLabel={SCREEN_CONSTANT.SHIPPING_BILL}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'soNumber1'}
                maxLength={35}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.soNumber1, SCREEN_CONSTANT.SHIPPING_BILL, isSaveClicked, transferOfChargeVM])

    const memoServiceReferenceNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.serviceReferenceNo ?? ''}
                fieldValue={searchCriteria?.serviceReferenceNo}
                fieldLabel={SCREEN_CONSTANT.SERVICE_REQUEST_REF}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'serviceReferenceNo'}
                maxLength={60}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.serviceReferenceNo, SCREEN_CONSTANT.SERVICE_REQUEST_REF, isSaveClicked, transferOfChargeVM])

    const memoCoVslVoy = useMemo(() =>
        <div className='flex-row-item' style={{ minWidth: "220px", maxWidth: "470px", width: "100%", marginBottom: "24px" }}>
            <HPHInputMask
                width='100%'
                label={SCREEN_CONSTANT.CO_VSL_VOY}
                autoFocus={false}
                value={{
                    co: searchCriteria.co ?? '',
                    vsl: searchCriteria.vsl ?? '',
                    voy: searchCriteria.voy ?? ''
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 6, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => transferOfChargeVM.onSearchCoVslVoyChange(e, {
                    co: 'co',
                    vsl: 'vsl',
                    voy: 'voy'
                })} />
        </div>
        , [SCREEN_CONSTANT.CO_VSL_VOY, searchCriteria.co, searchCriteria.vsl, searchCriteria.voy, transferOfChargeVM])

    const memoAgentCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "220px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.agentCode ?? ''}
                fieldValue={searchCriteria?.agentCode}
                fieldLabel={SCREEN_CONSTANT.AGENT_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'agentCode'}
                isTextFieldCapitalize={false}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.agentCode, SCREEN_CONSTANT.AGENT_NO, transferOfChargeVM])

    const memoCntrNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "220px", maxWidth: "470px", width: "100%", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                helpTextElement={"Use line break ( Enter ) as delimiter"}
                readOnlyValue={searchCriteria.cntrNos?.join('\n') || ''}
                fieldValue={searchCriteria?.cntrNos?.join('\n')}
                fieldLabel={SCREEN_CONSTANT.CONTAINER_NO_VEHICLE_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'cntrNos'}
                maxLength={780}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    transferOfChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.CONTAINER_NO_VEHICLE_NO, searchCriteria.cntrNos, transferOfChargeVM])

    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px", display: 'flex', flexDirection: 'column', height: 'calc(100vh - 62px)' }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{PortConstant.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={transferOfChargeVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ minHeight: '0', flex: '1', overflowY: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>

                        <CriteriaItemContainer>
                            {memoCntrNo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoOpsDateFromTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoBlNo1}
                            {memoSoNumber1}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoChargeType}
                            {memoSubChargeType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoChargeOnCompany}
                            {memoBillToCompany}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoOpsLine}
                            {memoState}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCoVslVoy}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoAgentCode}
                            {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && memoServiceReferenceNo}
                        </CriteriaItemContainer>
                        {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') &&
                            <CriteriaItemContainer>
                                {memoServiceRequestCode}
                                {memoSubServiceRequestCode}
                            </CriteriaItemContainer>}

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
            </SidebarActionBar>
        </div>
    );
}

export default memo(TransferOfChargeSearchPanel);
